import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { CalendarDays, Plus, Clock, User, ChevronLeft, ChevronRight } from 'lucide-react';
import DictationInput from '../components/DictationInput';
import { formatDate } from '../lib/utils';

const VIEWS = ['month', 'week', 'day'];

function getWeekDays(date) {
  const start = new Date(date);
  start.setDate(start.getDate() - start.getDay());
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
}

function getMonthDays(date) {
  const y = date.getFullYear(), m = date.getMonth();
  const first = new Date(y, m, 1), last = new Date(y, m + 1, 0);
  const startPad = first.getDay();
  const days = [];
  for (let i = startPad - 1; i >= 0; i--) {
    const d = new Date(y, m, -i);
    days.push({ date: d, outside: true });
  }
  for (let i = 1; i <= last.getDate(); i++) days.push({ date: new Date(y, m, i), outside: false });
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) days.push({ date: new Date(y, m + 1, i), outside: true });
  return days;
}

function dateKey(d) { return d.toISOString().split('T')[0]; }

export default function CalendarPage() {
  const { user, axiosAuth } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [users, setUsers] = useState([]);
  const [view, setView] = useState('month');
  const [current, setCurrent] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: '', time: '', type: 'individual', with_user_id: '', notes: '', outcome: '' });
  const [selected, setSelected] = useState(null);
  const api = axiosAuth();
  const otherUsers = useMemo(() => users.filter(u => u.id !== user.id), [users, user.id]);

  const load = () => {
    api.get('/appointments').then(r => setAppointments(r.data)).catch(() => {});
    api.get('/users').then(r => setUsers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.date || !form.time) return toast.error('Date and time required');
    try {
      await api.post('/appointments', form);
      toast.success('Appointment scheduled!');
      setOpen(false);
      setForm({ date: '', time: '', type: 'individual', with_user_id: '', notes: '', outcome: '' });
      load();
    } catch { toast.error('Failed to create'); }
  };

  const handleStatus = async (id, status) => {
    try {
      await api.put(`/appointments/${id}`, { status });
      toast.success(`Appointment ${status}`);
      setSelected(null);
      load();
    } catch { toast.error('Failed to update'); }
  };

  const apptsByDate = {};
  appointments.forEach(a => {
    if (!apptsByDate[a.date]) apptsByDate[a.date] = [];
    apptsByDate[a.date].push(a);
  });

  const navigate = (dir) => {
    const d = new Date(current);
    if (view === 'month') d.setMonth(d.getMonth() + dir);
    else if (view === 'week') d.setDate(d.getDate() + dir * 7);
    else d.setDate(d.getDate() + dir);
    setCurrent(d);
  };

  const today = dateKey(new Date());
  const statusColor = { scheduled: 'bg-sky-100 text-sky-700 border-sky-200', completed: 'bg-emerald-100 text-emerald-700 border-emerald-200', cancelled: 'bg-red-100 text-red-700 border-red-200' };
  const getUserName = (id) => users.find(u => u.id === id)?.full_name || '';

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Calendar</h1>
          <p className="text-muted-foreground text-sm mt-1">View and manage appointments</p>
        </div>
        {user.role !== 'court_officer' && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="new-appt-btn"><Plus size={16} /> New Appointment</Button></DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Schedule Appointment</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-sm">Date</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="cal-date" /></div>
                  <div className="space-y-1.5"><Label className="text-sm">Time</Label><Input type="time" value={form.time} onChange={e => setForm({...form, time: e.target.value})} data-testid="cal-time" /></div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Meeting With</Label>
                  <Select value={form.with_user_id} onValueChange={v => setForm({...form, with_user_id: v})}>
                    <SelectTrigger data-testid="cal-with-user"><SelectValue placeholder="Select person" /></SelectTrigger>
                    <SelectContent>{otherUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.role})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Type</Label>
                  <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Individual Session</SelectItem>
                      <SelectItem value="group">Group Session</SelectItem>
                      <SelectItem value="assessment">Assessment</SelectItem>
                      <SelectItem value="court">Court Related</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Notes (optional)</Label>
                  <DictationInput value={form.notes} onChange={(cleaned) => setForm(f => ({...f, notes: cleaned}))} rows={2} placeholder="Add notes..." data-testid="calendar-notes" />
                </div>
                <Button onClick={handleCreate} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="cal-create-btn">Schedule</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* View Switcher + Navigation */}
      <Card className="border-0 shadow-sm rounded-2xl">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="cal-prev"><ChevronLeft size={18} /></Button>
              <h2 className="text-lg font-semibold min-w-[180px] text-center" style={{ fontFamily: 'Manrope' }}>
                {view === 'day' ? current.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                  : view === 'week' ? `Week of ${getWeekDays(current)[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                  : current.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => navigate(1)} data-testid="cal-next"><ChevronRight size={18} /></Button>
            </div>
            <div className="flex bg-muted rounded-lg p-0.5">
              {VIEWS.map(v => (
                <button key={v} onClick={() => setView(v)} className={`px-3 py-1 text-xs font-medium rounded-md capitalize transition-all ${view === v ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid={`cal-view-${v}`}>
                  {v}
                </button>
              ))}
            </div>
          </div>

          {/* Month View */}
          {view === 'month' && (
            <div>
              <div className="grid grid-cols-7 text-center text-xs text-muted-foreground font-medium mb-1">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d} className="py-1">{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-px bg-border rounded-xl overflow-hidden">
                {getMonthDays(current).map((d, i) => {
                  const key = dateKey(d.date);
                  const dayAppts = apptsByDate[key] || [];
                  const isToday = key === today;
                  return (
                    <div key={`item-${i}`} className={`min-h-[80px] p-1.5 bg-white ${d.outside ? 'opacity-40' : ''}`} onClick={() => { setCurrent(d.date); setView('day'); }} style={{ cursor: 'pointer' }}>
                      <p className={`text-xs font-medium mb-1 w-6 h-6 flex items-center justify-center rounded-full ${isToday ? 'bg-teal-600 text-white' : ''}`}>{d.date.getDate()}</p>
                      {dayAppts.slice(0, 2).map(a => (
                        <div key={a.id} className={`text-[10px] px-1 py-0.5 rounded mb-0.5 truncate border ${statusColor[a.status]}`} onClick={e => { e.stopPropagation(); setSelected(a); }}>
                          {a.time} {a.type}
                        </div>
                      ))}
                      {dayAppts.length > 2 && <p className="text-[10px] text-muted-foreground">+{dayAppts.length - 2} more</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Week View */}
          {view === 'week' && (
            <div className="grid grid-cols-7 gap-2">
              {getWeekDays(current).map(d => {
                const key = dateKey(d);
                const dayAppts = apptsByDate[key] || [];
                const isToday = key === today;
                return (
                  <div key={key} className="min-h-[150px]">
                    <p className={`text-xs font-medium text-center mb-2 py-1 rounded-lg ${isToday ? 'bg-teal-600 text-white' : 'bg-muted'}`}>
                      {d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' })}
                    </p>
                    {dayAppts.map(a => (
                      <div key={a.id} onClick={() => setSelected(a)} className={`text-xs p-2 rounded-lg mb-1 border cursor-pointer hover:shadow-sm ${statusColor[a.status]}`}>
                        <p className="font-medium">{a.time}</p>
                        <p className="capitalize truncate">{a.type}</p>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          {/* Day View */}
          {view === 'day' && (
            <div className="space-y-2">
              {(apptsByDate[dateKey(current)] || []).length > 0 ? (apptsByDate[dateKey(current)] || []).map(a => (
                <div key={a.id} onClick={() => setSelected(a)} className={`p-4 rounded-xl border cursor-pointer hover:shadow-sm ${statusColor[a.status]}`}>
                  <div className="flex items-center gap-3">
                    <CalendarDays size={18} />
                    <div className="flex-1">
                      <p className="font-medium capitalize">{a.type} Session &mdash; {a.time}</p>
                      {a.with_user_id && <p className="text-sm"><User size={12} className="inline mr-1" />{getUserName(a.with_user_id)}</p>}
                      {a.notes && <p className="text-sm mt-1 opacity-80">{a.notes}</p>}
                    </div>
                    <Badge className={statusColor[a.status]}>{a.status}</Badge>
                  </div>
                </div>
              )) : (
                <div className="text-center py-12 text-muted-foreground">
                  <CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>No appointments for this day</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Appointment Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={v => !v && setSelected(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Appointment Details</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4 pt-2" data-testid="appt-detail">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-xs text-muted-foreground">Type</p><p className="font-medium capitalize">{selected.type} Session</p></div>
                <div><p className="text-xs text-muted-foreground">Status</p><Badge className={statusColor[selected.status]}>{selected.status}</Badge></div>
                <div><p className="text-xs text-muted-foreground">Date</p><p className="font-medium">{selected.date}</p></div>
                <div><p className="text-xs text-muted-foreground">Time</p><p className="font-medium">{selected.time}</p></div>
                {selected.with_user_id && <div className="col-span-2"><p className="text-xs text-muted-foreground">Meeting With</p><p className="font-medium">{getUserName(selected.with_user_id)}</p></div>}
              </div>
              {selected.notes && <div><p className="text-xs text-muted-foreground mb-1">Notes</p><p className="text-sm bg-muted/50 rounded-lg p-3">{selected.notes}</p></div>}
              {selected.outcome && <div><p className="text-xs text-muted-foreground mb-1">Outcome</p><p className="text-sm bg-emerald-50 rounded-lg p-3 border border-emerald-200">{selected.outcome}</p></div>}
              {selected.status === 'scheduled' && user.role !== 'court_officer' && (
                <div className="flex gap-2">
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full text-sm" onClick={() => handleStatus(selected.id, 'completed')}>Mark Completed</Button>
                  <Button variant="outline" className="rounded-full text-sm text-red-600" onClick={() => handleStatus(selected.id, 'cancelled')}>Cancel</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
